import {Badge} from '@dropbox/dig-components/badge';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import styles from './GroupTabs.module.css';

export interface GroupTab {
  id: string;
  label: string;
  count: number;
  disabled: boolean;
}

export const GroupTabs = ({
  tabs,
  activeTab,
  onTabClick,
  totalCount,
  showAllTab = true,
  showCounts = true,
  pressedState = false,
}: {
  tabs: GroupTab[];
  activeTab: string | null;
  onTabClick: (tabId: string | null) => void;
  totalCount: number;
  showAllTab?: boolean;
  showCounts?: boolean;
  pressedState?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const allMeasureRef = useRef<HTMLButtonElement>(null);
  const overflowMeasureRef = useRef<HTMLButtonElement>(null);
  const tabMeasureRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [overflowTabIds, setOverflowTabIds] = useState<string[]>([]);
  const FIT_BUFFER_PX = 8;

  const recomputeOverflow = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 0;
    const allWidth = allMeasureRef.current?.offsetWidth ?? 0;
    const overflowWidth = overflowMeasureRef.current?.offsetWidth ?? 0;
    const allSlotWidth = showAllTab ? allWidth : 0;
    const widthLimit = containerWidth - FIT_BUFFER_PX;

    if (
      containerWidth <= 0 ||
      tabs.length === 0 ||
      (showAllTab && allWidth <= 0) ||
      overflowWidth <= 0
    ) {
      setOverflowTabIds([]);
      return;
    }

    const tabWidths = tabs.map((tab) => ({
      id: tab.id,
      width: tabMeasureRefs.current[tab.id]?.offsetWidth ?? 0,
    }));
    const totalTabsWidth = tabWidths.reduce((sum, tab) => sum + tab.width, 0);
    if (allSlotWidth + totalTabsWidth <= widthLimit) {
      setOverflowTabIds([]);
      return;
    }

    const visibleIds: string[] = [];
    let used = allSlotWidth;
    for (const tab of tabWidths) {
      if (tab.width <= 0) continue;
      if (used + tab.width + overflowWidth <= widthLimit) {
        visibleIds.push(tab.id);
        used += tab.width;
      } else {
        break;
      }
    }

    if (visibleIds.length === 0 && tabs.length > 0) {
      visibleIds.push(tabs[0].id);
    }

    if (activeTab && tabs.some((tab) => tab.id === activeTab) && !visibleIds.includes(activeTab)) {
      const widthById = new Map(tabWidths.map(({id, width}) => [id, width]));
      const tabIndexById = new Map(tabs.map((tab, index) => [tab.id, index]));
      const activeWidth = widthById.get(activeTab) ?? 0;
      const adjustedVisibleIds = [...visibleIds];

      let currentWidth =
        allSlotWidth +
        adjustedVisibleIds.reduce((sum, id) => sum + (widthById.get(id) ?? 0), 0) +
        overflowWidth +
        activeWidth;
      while (adjustedVisibleIds.length > 0 && currentWidth > widthLimit) {
        const removedId = adjustedVisibleIds.pop();
        if (!removedId) break;
        currentWidth -= widthById.get(removedId) ?? 0;
      }

      adjustedVisibleIds.push(activeTab);
      const sortedVisibleIds = Array.from(new Set(adjustedVisibleIds)).sort(
        (a, b) => (tabIndexById.get(a) ?? 0) - (tabIndexById.get(b) ?? 0)
      );

      let finalWidth =
        allSlotWidth +
        sortedVisibleIds.reduce((sum, id) => sum + (widthById.get(id) ?? 0), 0) +
        overflowWidth;
      while (finalWidth > widthLimit) {
        const removableIndex = sortedVisibleIds.findIndex((id) => id !== activeTab);
        if (removableIndex === -1) break;
        const [removed] = sortedVisibleIds.splice(removableIndex, 1);
        finalWidth -= widthById.get(removed) ?? 0;
      }

      visibleIds.splice(0, visibleIds.length, ...sortedVisibleIds);
    }

    const visibleIdSet = new Set(visibleIds);
    const nextOverflow = tabs.filter((tab) => !visibleIdSet.has(tab.id)).map((tab) => tab.id);
    setOverflowTabIds(nextOverflow);
  }, [tabs, activeTab, showAllTab]);

  useEffect(() => {
    recomputeOverflow();
  }, [recomputeOverflow]);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => recomputeOverflow());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [recomputeOverflow]);

  const overflowIdSet = useMemo(() => new Set(overflowTabIds), [overflowTabIds]);
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => !overflowIdSet.has(tab.id)),
    [tabs, overflowIdSet]
  );
  const overflowTabs = useMemo(
    () => tabs.filter((tab) => overflowIdSet.has(tab.id)),
    [tabs, overflowIdSet]
  );
  const hasOverflow = overflowTabs.length > 0;
  const hasActiveOverflowTab = activeTab != null && overflowIdSet.has(activeTab);
  const getTabAriaLabel = useCallback(
    (label: string, count: number) => (showCounts ? `${label} (${count})` : label),
    [showCounts]
  );

  return (
    <div ref={containerRef} className={styles.groupTabs}>
      {showAllTab && (
        <button
          type="button"
          className={`${styles.groupTab} ${activeTab === null ? styles.groupTabActive : ''}`}
          onClick={() => onTabClick(null)}
          aria-label={getTabAriaLabel(t('all').toString(), totalCount)}
          aria-pressed={pressedState ? activeTab === null : undefined}
        >
          <Text className={styles.groupTabLabel}>{t('all')}</Text>
          {showCounts ? <Badge className={styles.groupTabCountBadge}>{totalCount}</Badge> : null}
        </button>
      )}
      {visibleTabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={`${styles.groupTab} ${activeTab === tab.id ? styles.groupTabActive : ''} ${tab.disabled ? styles.groupTabDisabled : ''}`}
          onClick={() => !tab.disabled && onTabClick(tab.id)}
          disabled={tab.disabled}
          aria-label={getTabAriaLabel(tab.label, tab.count)}
          aria-pressed={pressedState ? activeTab === tab.id : undefined}
        >
          <Text className={styles.groupTabLabel}>{tab.label}</Text>
          {showCounts ? <Badge className={styles.groupTabCountBadge}>{tab.count}</Badge> : null}
        </button>
      ))}
      {hasOverflow && (
        <Menu.Wrapper>
          {({getContentProps, getTriggerProps}) => (
            <>
              <button
                {...getTriggerProps()}
                type="button"
                className={`${styles.groupOverflowButton} ${hasActiveOverflowTab ? styles.groupTabActive : ''}`}
                aria-label={t('more_title')}
              >
                <UIIcon src={MoreHorizontalLine} size="medium" />
              </button>
              <Menu.Content {...getContentProps()} placement="bottom-end">
                <Menu.Segment>
                  {overflowTabs.map((tab) => (
                    <Menu.ActionItem
                      key={tab.id}
                      disabled={tab.disabled}
                      onClick={() => !tab.disabled && onTabClick(tab.id)}
                      withRightAccessory={
                        showCounts ? (
                          <span className={styles.groupOverflowAccessory}>
                            <Badge className={styles.groupOverflowCountBadge}>{tab.count}</Badge>
                            {activeTab === tab.id ? (
                              <UIIcon src={CheckmarkLine} size="small" />
                            ) : null}
                          </span>
                        ) : activeTab === tab.id ? (
                          <UIIcon src={CheckmarkLine} size="small" />
                        ) : undefined
                      }
                    >
                      {tab.label}
                    </Menu.ActionItem>
                  ))}
                </Menu.Segment>
              </Menu.Content>
            </>
          )}
        </Menu.Wrapper>
      )}

      <div className={styles.groupTabsMeasure} aria-hidden>
        {showAllTab && (
          <button ref={allMeasureRef} type="button" className={styles.groupTab}>
            <Text className={styles.groupTabLabel}>{t('all')}</Text>
            {showCounts ? <Badge className={styles.groupTabCountBadge}>{totalCount}</Badge> : null}
          </button>
        )}
        {tabs.map((tab) => (
          <button
            key={`measure-${tab.id}`}
            ref={(element) => {
              tabMeasureRefs.current[tab.id] = element;
            }}
            type="button"
            className={styles.groupTab}
          >
            <Text className={styles.groupTabLabel}>{tab.label}</Text>
            {showCounts ? <Badge className={styles.groupTabCountBadge}>{tab.count}</Badge> : null}
          </button>
        ))}
        <button ref={overflowMeasureRef} type="button" className={styles.groupOverflowButton}>
          <UIIcon src={MoreHorizontalLine} size="medium" />
        </button>
      </div>
    </div>
  );
};
